import { Link, useLocation } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';
import { useIsAdmin, useIsInstructor, useIsSchoolAdmin, useIsSuperAdmin } from '@/hooks/useUserRoles';
import { Button } from '@/components/ui/button';
import { Menu, X, User, Heart, Shield, GraduationCap, School, MapPin, Briefcase, DollarSign, Activity, Check, Plus, Settings2 } from 'lucide-react';
import paletteLogo from '@/assets/palette-logo.png';
import { useState, useEffect } from 'react';
import { useSavedAddresses, useSetDefaultAddress } from '@/hooks/useSavedAddresses';

import { CartIcon } from '@/components/CartIcon';
import { NotificationsBell } from '@/components/notifications/NotificationsBell';
import LoginDialog from '@/components/auth/LoginDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function LanguageToggle({ compact = false }: { compact?: boolean }) {
  const { locale, setLocale } = useLocale();
  const next = locale === 'ar' ? 'en' : 'ar';
  return (
    <button
      type="button"
      translate="no"
      onClick={() => setLocale(next)}
      aria-label={locale === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
      className={`notranslate inline-flex items-center justify-center rounded-full bg-background/80 hover:bg-background text-foreground shadow-md border border-border/40 backdrop-blur font-bold transition-colors ${
        compact ? 'h-9 min-w-9 px-2.5 text-xs' : 'h-10 px-4 text-sm'
      }`}
    >
      {locale === 'ar' ? 'EN' : 'عربي'}
    </button>
  );
}

export function Header() {
  const { t, locale } = useLocale();
  const { user, signOut } = useAuth();
  const { hasRole: isAdmin } = useIsAdmin();
  const { hasRole: isInstructor } = useIsInstructor();
  const { hasRole: isSchoolAdmin } = useIsSchoolAdmin();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isArabic = locale === 'ar';
  const { data: savedAddresses } = useSavedAddresses();
  const defaultAddress = savedAddresses?.find(a => a.is_default) || savedAddresses?.[0];
  const [addressOpen, setAddressOpen] = useState(false);
  const setDefaultAddress = useSetDefaultAddress();
  const [loginOpen, setLoginOpen] = useState(false);

  // Transparent over hero on homepage until user scrolls
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!isHome) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);
  const overlay = isHome && !scrolled;

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        overlay
          ? 'bg-transparent border-b border-transparent shadow-none'
          : 'bg-white/30 backdrop-blur-2xl backdrop-saturate-150 border-b border-white/30 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.08)]'
      }`}
    >
      <div className="container mx-auto px-4">
        <nav className="grid grid-cols-[auto_1fr_auto] items-center h-16 md:h-20 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src={paletteLogo} alt="Palette Kuwait logo" className="w-12 h-12 object-contain" />
          </Link>

          {/* Desktop Navigation - Centered (links moved to home hero) */}
          <div className="hidden md:flex items-center justify-center gap-8" />
          <div className="md:hidden" />

          {/* Auth & Actions */}
          <div className="hidden md:flex items-center gap-3 justify-self-end">
            {/* Default address indicator */}
            {user && (
              <button
                type="button"
                onClick={() => setAddressOpen(true)}
                className={`flex items-center gap-1.5 max-w-[220px] px-3 py-1.5 rounded-full border transition-colors ${
                  overlay
                    ? 'bg-white/20 hover:bg-white/30 border-white/40 backdrop-blur-md text-white'
                    : 'bg-background/60 hover:bg-background/90 border-border/50'
                }`}
                title={defaultAddress?.address || (isArabic ? 'إضافة عنوان' : 'Add address')}
              >
                <MapPin className={`w-4 h-4 shrink-0 ${overlay ? 'text-white' : 'text-primary'}`} />
                <span className={`text-xs font-medium truncate ${overlay ? 'text-white drop-shadow' : 'text-foreground/80'}`}>
                  {defaultAddress
                    ? (defaultAddress.label || defaultAddress.address)
                    : (isArabic ? 'اختر عنوان' : 'Select address')}
                </span>
              </button>
            )}

            {/* Favorites Icon */}
            <Button variant="ghost" size="icon" className="rounded-full" asChild>
              <Link to="/favorites" aria-label={t('favorites')}>
                <Heart className="w-5 h-5" />
              </Link>
            </Button>

            {/* Notifications Bell (admins) */}
            {isAdmin && <NotificationsBell />}

            {/* Basket Icon */}
            <CartIcon />

            <LanguageToggle />


            {user ? (
              <AccountMenu
                isArabic={isArabic}
                isAdmin={isAdmin}
                isInstructor={isInstructor}
                isSchoolAdmin={isSchoolAdmin}
                signOut={signOut}
                logoutLabel={t('logout')}
              />
            ) : (
              <>
                <Button variant="ghost" onClick={() => setLoginOpen(true)}>
                  {t('login')}
                </Button>
                <Button variant="outline" className="rounded-full" asChild>
                  <Link to="/signup">{t('createAccount')}</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile actions */}
          <div className="md:hidden flex items-center gap-2 justify-self-end">
            <LanguageToggle compact />
            {user && (
              <button
                type="button"
                onClick={() => setAddressOpen(true)}
                className={`flex items-center gap-1 max-w-[120px] px-2.5 py-1.5 rounded-full border ${
                  overlay
                    ? 'bg-white/20 border-white/40 backdrop-blur-md text-white'
                    : 'bg-background/70 border-border/50'
                }`}
                title={defaultAddress?.address || (isArabic ? 'إضافة عنوان' : 'Add address')}
              >
                <MapPin className={`w-3.5 h-3.5 shrink-0 ${overlay ? 'text-white' : 'text-primary'}`} />
                <span className={`text-[11px] font-medium truncate ${overlay ? 'text-white drop-shadow' : 'text-foreground/80'}`}>
                  {defaultAddress
                    ? (defaultAddress.label || defaultAddress.address)
                    : (isArabic ? 'اختر عنوان' : 'Select address')}
                </span>
              </button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full h-9 w-9 bg-white/80 hover:bg-white text-foreground shadow-md border border-white/60 backdrop-blur"
              asChild
            >
              <Link to="/favorites" aria-label={t('favorites')}>
                <Heart className="w-5 h-5" />
              </Link>
            </Button>
            {isAdmin && (
              <div className="rounded-full bg-white/80 hover:bg-white text-foreground shadow-md border border-white/60 backdrop-blur">
                <NotificationsBell />
              </div>
            )}
            <div className="rounded-full bg-white/80 hover:bg-white shadow-md border border-white/60 backdrop-blur">
              <CartIcon compact />
            </div>
            {!user && (
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full h-9 w-9 bg-white/80 hover:bg-white text-foreground shadow-md border border-white/60 backdrop-blur"
                onClick={() => setLoginOpen(true)}
                aria-label={t('login')}
              >
                <User className="w-5 h-5" />
              </Button>
            )}
            {user && (
              <AccountMenu
                isArabic={isArabic}
                isAdmin={isAdmin}
                isInstructor={isInstructor}
                isSchoolAdmin={isSchoolAdmin}
                signOut={signOut}
                logoutLabel={t('logout')}
                compact
              />
            )}

          </div>
        </nav>

        {/* Mobile Menu (disabled — actions live in header icons) */}
        {false && mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/30 animate-fade-in">
            <div className="flex flex-col gap-4">
              <Link 
                to="/activities" 
                className="text-foreground/80 hover:text-primary transition-colors font-medium py-2 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('activities')}
              </Link>
              <Link 
                to="/schools" 
                className="text-foreground/80 hover:text-primary transition-colors font-medium py-2 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                {isArabic ? 'المدارس' : 'Schools'}
              </Link>
              <div className="flex flex-col gap-2 pt-4 border-t border-white/30">
                {user ? (
                  <>
                    {isInstructor && (
                      <Button variant="outline" asChild className="rounded-full border-primary text-primary">
                        <Link to="/instructor" onClick={() => setMobileMenuOpen(false)}>
                          <GraduationCap className="w-4 h-4 me-2" />
                          {isArabic ? 'المدرب' : 'Instructor Panel'}
                        </Link>
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="outline" asChild className="rounded-full border-primary text-primary">
                        <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                          <Shield className="w-4 h-4 me-2" />
                          {isArabic ? 'المسؤول' : 'Admin Panel'}
                        </Link>
                      </Button>
                    )}
                    {isSchoolAdmin && (
                      <Button variant="outline" asChild className="rounded-full border-primary text-primary">
                        <Link to="/dashboard/schools" onClick={() => setMobileMenuOpen(false)}>
                          <School className="w-4 h-4 me-2" />
                          {isArabic ? 'المدرسة' : 'School Panel'}
                        </Link>
                      </Button>
                    )}
                    <Button variant="outline" asChild className="rounded-full border-primary text-primary">
                      <Link to="/dashboard/settings" onClick={() => setMobileMenuOpen(false)}>
                        <User className="w-4 h-4 me-2" />
                        {isArabic ? 'حسابي' : 'My Account'}
                      </Link>
                    </Button>
                    <Button variant="ghost" onClick={() => { signOut(); setMobileMenuOpen(false); }}>
                      {t('logout')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" asChild className="rounded-full">
                      <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                        {t('login')}
                      </Link>
                    </Button>
                    <Button asChild className="rounded-full">
                      <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                        {t('signup')}
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Address picker dialog */}
      <Dialog open={addressOpen} onOpenChange={setAddressOpen}>
        <DialogContent className="max-w-md bg-white/80 backdrop-blur-2xl border border-white/50 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-start flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              {isArabic ? 'اختر عنوانك' : 'Choose your address'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-2 max-h-[50vh] overflow-y-auto py-2">
            {savedAddresses && savedAddresses.length > 0 ? (
              savedAddresses.map((addr) => {
                const isActive = defaultAddress?.id === addr.id;
                return (
                  <button
                    key={addr.id}
                    type="button"
                    onClick={() => {
                      setDefaultAddress.mutate(addr.id, {
                        onSuccess: () => setAddressOpen(false),
                      });
                    }}
                    className={`w-full text-start flex items-start gap-3 p-3 rounded-2xl border transition-colors ${
                      isActive
                        ? 'bg-primary/10 border-primary/40'
                        : 'bg-background/60 border-border/50 hover:bg-background/90'
                    }`}
                  >
                    <MapPin className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{addr.label || addr.address}</div>
                      {addr.label && (
                        <div className="text-xs text-muted-foreground truncate">{addr.address}</div>
                      )}
                    </div>
                    {isActive && <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />}
                  </button>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground text-center py-6">
                {isArabic ? 'لا توجد عناوين محفوظة بعد' : 'No saved addresses yet'}
              </p>
            )}
          </div>

          <div className="flex gap-2 pt-2 border-t border-border/40">
            <Button asChild variant="outline" className="flex-1 rounded-full" onClick={() => setAddressOpen(false)}>
              <Link to="/dashboard/addresses">
                <Settings2 className="w-4 h-4 me-2" />
                {isArabic ? 'إدارة العناوين' : 'Manage addresses'}
              </Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
    </header>
  );
}

function AccountMenu({
  isArabic,
  isAdmin,
  isInstructor,
  isSchoolAdmin,
  signOut,
  logoutLabel,
  compact = false,
}: {
  isArabic: boolean;
  isAdmin: boolean;
  isInstructor: boolean;
  isSchoolAdmin: boolean;
  signOut: () => void | Promise<void>;
  logoutLabel: string;
  compact?: boolean;
}) {
  // Show ALL panels the user has access to
  const { hasRole: isSuperAdmin } = useIsSuperAdmin();
  const panels: { to: string; label: string; icon: JSX.Element }[] = [];
  panels.push({
    to: '/dashboard/settings',
    label: isArabic ? 'حسابي' : 'My Account',
    icon: <User className="w-4 h-4" />,
  });
  if (isInstructor) {
    panels.push({
      to: '/instructor',
      label: isArabic ? 'لوحة المدرب' : 'Instructor Panel',
      icon: <GraduationCap className="w-4 h-4" />,
    });
  }
  if (isSchoolAdmin) {
    panels.push({
      to: '/dashboard/schools',
      label: isArabic ? 'لوحة المدرسة' : 'School Panel',
      icon: <School className="w-4 h-4" />,
    });
  }
  if (isAdmin) {
    panels.push({
      to: '/admin',
      label: isArabic ? 'لوحة المسؤول' : 'Admin Panel',
      icon: <Shield className="w-4 h-4" />,
    });
  }
  if (isSuperAdmin) {
    panels.push({
      to: '/ceo',
      label: isArabic ? 'لوحة المسؤول الأعلى' : 'Super Admin Panel',
      icon: <Briefcase className="w-4 h-4" />,
    });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={compact ? 'rounded-full h-9 w-9' : 'rounded-full'}
          aria-label={isArabic ? 'حسابي' : 'My account'}
        >
          <User className="w-5 h-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-popover">
        {panels.map((p) => (
          <DropdownMenuItem key={p.to} asChild>
            <Link to={p.to} className="flex items-center gap-2">
              {p.icon}
              {p.label}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => signOut()}>
          {logoutLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

